import { type FC } from 'react';

import { Button, Flex, HStack, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from '@/components/Form';

import { type FormFieldsEmailFilters, zFormFieldsEmailFilter } from './schemas';

type FormInputs = {
  email?: string;
};

type EmailFilterComponentProps = {
  emailInitialValue: string | null;
  onSubmit: (email: string | null) => void;
  onclose: () => void;
  onClearEmailFilter?: () => void;
};

export const EmailFilterComponent: FC<EmailFilterComponentProps> = ({
  emailInitialValue,
  onSubmit,
  onclose,
  onClearEmailFilter,
}) => {
  const { t } = useTranslation(['common']);

  const form = useForm<FormFieldsEmailFilters>({
    mode: 'onBlur',
    resolver: zodResolver(zFormFieldsEmailFilter()),
    defaultValues: {
      email: emailInitialValue ?? undefined,
    },
  });

  const onFormSubmit: SubmitHandler<FormInputs> = async (data) => {
    const emailFilterObj = {
      email: data.email?.trim(),
    };

    onSubmit(emailFilterObj.email ?? null);
    onclose();
  };

  return (
    <Flex flexDir="column" p={3}>
      <HStack justify="space-between" mb={2}>
        <Text size="sm" fontWeight="medium" color="gray.700">
          {t('common:information.email')}
        </Text>
        {onClearEmailFilter && emailInitialValue && (
          <Button
            size="sm"
            color="brand.500"
            onClick={(e) => {
              e.stopPropagation();
              onClearEmailFilter();
            }}
          >
            {t('common:actions.clear')}
          </Button>
        )}
      </HStack>

      <Form {...form} onSubmit={onFormSubmit}>
        <FormField
          placeholder={t('common:information.email')}
          type="email"
          control={form.control}
          name="email"
          size="md"
        />

        <Button type="submit" mt={4} variant="@primary" size="sm" w="full">
          {t('common:actions.applyFilter')}
        </Button>
      </Form>
    </Flex>
  );
};
